import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './quotes.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { statusVariant } from '../../../components/statusPill';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import PreLoginDocsPanel, { type DocsOwner } from '../../settings/preLoginSettings/PreLoginDocsPanel';
import { downloadQuoteReport, fetchQuote, reportErrorMessage } from './detailApi';
import { CHARGE_TYPE, type QuoteAnalysisRow, type QuoteDetail, type QuoteItemRow } from './detailTypes';

// ─────────────────────────────────────────────────────────────────────────────
// CRM ▸ Accounts ▸ Quotes ▸ Canceled Quotes — the READ-ONLY detail view.
// Opened by double-clicking (or Enter on) a row of `CanceledQuotesPage`.
//
// Legacy ALPACA opens a canceled quote in its `CRMQuotes_DetailView` with
// AllowEdit="False" — the SAME layout as the Open Quote detail, shown read-only.
// This is a BRAND-NEW read-only component (owner request 2026-10-29: "create
// newly for the Details view controls, don't map with any existing forms") — it
// reuses only the DATA layer + shared infra (the `GET /api/v1/quotes/{id}`
// fetch, the shared documents panel, the `.sd-*` detail stylesheet), never the
// editable `QuoteDetailPage`. There are NO New / Edit / Delete / Save actions —
// every field and both child grids are read-only.
//
// The field groups and the four tabs follow the legacy detail view, field for
// field, so a canceled quote reads exactly like its Open-Quote counterpart:
//   Summary · Totals · Contacts · Bill To Address · Report to Address ·
//   Account Management  +  Analysis Pricing · Item Charge · Attachment · Notes.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Open Quotes';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

type Tab = 'analysis' | 'items' | 'attachment' | 'note';

/** The shared Attachment | Notes panel, pointed at a quote (read-only here). */
const QUOTE_DOCS: DocsOwner = {
  apiBase: '/api/v1/quotes',
  formName: 'Open Quotes',
  businessObject: 'Open Quote',
  exportPrefix: 'Quote',
  noteTabLabel: 'Notes',
  countFromRows: true,
  legacyListBar: true,
};

interface Props {
  /** The quote to show. */
  id: string;
  /** Its QuoteID, for the header before the fetch returns. */
  quoteId?: string | null;
  /** The list's row ids, for the prev/next record arrows (BO UI Standard §4). */
  siblingIds?: string[];
  onNavigate?: (id: string) => void;
  /** Back to the list. */
  onClose: () => void;
  onHome?: () => void;
}

export default function CanceledQuoteDetailPage({
  id, quoteId, siblingIds = [], onNavigate, onClose, onHome,
}: Props) {
  const notify = useNotify();
  const [quote, setQuote]     = useState<QuoteDetail | null>(null);
  const [rows, setRows]       = useState<QuoteAnalysisRow[]>([]);
  const [items, setItems]     = useState<QuoteItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState<Tab>('analysis');
  const [docCounts, setDocCounts] = useState({ attachments: 0, notes: 0 });
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const b = await fetchQuote(id);
      setQuote(b.header);
      setRows(b.rows);
      setItems(b.items);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the quote.');
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => { void load(); }, [load]);

  // Prev / next record — walk the list's row ids (same pattern as the sibling
  // detail, QuoteDetailPage:810).
  const navPos  = siblingIds.indexOf(id);
  const navPrev = navPos > 0 ? siblingIds[navPos - 1] ?? null : null;
  const navNext = navPos >= 0 && navPos < siblingIds.length - 1 ? siblingIds[navPos + 1] ?? null : null;

  /** Preview — the SAME `GET /quotes/{id}/report` the Open Quotes list uses. */
  async function preview() {
    setBusy(true);
    try {
      const filename = await downloadQuoteReport(id);
      notify.success(`${filename} downloaded.`);
    } catch (e) {
      notify.error((await reportErrorMessage(e)) ?? 'Failed to generate the quote report.');
    } finally {
      setBusy(false);
    }
  }

  const tip = (label: string, combo: string) => `${label} (${formatCombo(combo)})`;

  // Preview + Refresh are form-specific here, so they register their own hotkeys
  // (standing rule 14); there is no toolbar component on this read-only page.
  useHotkeys([
    { combo: 'alt+p', label: 'Preview', group: 'Canceled Quote', order: 1,
      disabled: busy, run: () => void preview() },
    { combo: 'alt+r', label: 'Refresh', group: 'Canceled Quote', order: 2,
      disabled: loading, run: () => void load() },
    { combo: 'esc',   label: 'Close',   group: 'Canceled Quote', order: 3,
      run: onClose },
  ]);

  // ── Read-only field helpers — the shared detail-view markup (`.sd-row` >
  // `.sd-label` + `.sd-input readonly`), so label width, the 26px control and
  // the grey read-only cue all come from SettingsDetailPage.css (standing rule
  // 12), identical to the editable form's fields. ────────────────────────────
  function field(label: string, control: ReactNode, key?: string) {
    return (
      <div className="sd-row" key={key ?? label}>
        <label className="sd-label">{label}</label>
        {control}
      </div>
    );
  }
  const roText  = (v: string | null | undefined) =>
    <input type="text" className="sd-input readonly" readOnly value={v ?? ''} />;
  const roMoney = (v: number) =>
    <input type="text" className="sd-input readonly qt-money" readOnly value={money.format(v)} />;
  const roDate  = (v: string | null | undefined) =>
    <input type="text" className="sd-input readonly" readOnly value={fmtLocalDate(v ?? null)} />;
  const roArea  = (v: string | null | undefined) =>
    <textarea rows={3} className="sd-input sd-input--tall readonly" readOnly value={v ?? ''} />;
  /** A read-only tick box, laid out as the shared page lays one out. */
  const check = (label: string, checked: boolean, key?: string) => (
    <div className="sd-row sd-row--check" key={key ?? label}>
      <span className="sd-label" aria-hidden="true">&nbsp;</span>
      <label className="sd-check">
        <input type="checkbox" checked={checked} readOnly disabled />
        {label}
      </label>
    </div>
  );

  /** One titled card of fields — the shared `.sd-body--cards` layout. */
  function section(title: string, left: ReactNode[], right?: ReactNode[], extra?: ReactNode) {
    const col = (list: ReactNode[]) => (
      <div className="sd-card-col">
        <div className="sd-grid sd-grid--single sd-grid--side">{list.filter(Boolean)}</div>
      </div>
    );
    return (
      <section className="sd-card" aria-label={title} key={title}>
        <div className="sd-card-h">
          <h3 className="sd-section-h">{title}</h3>
          {extra}
        </div>
        {right ? <div className="sd-card-cols">{col(left)}{col(right)}</div> : col(left)}
      </section>
    );
  }

  // ── Child grids — read-only mirrors of the editable form's columns, with the
  // editors and cell pickers removed (QuoteDetailPage:840 / :885). ────────────
  const rowColumns: AppColumn<QuoteAnalysisRow>[] = useMemo(() => [
    { field: 'testDescription', header: 'TestDescription', width: 170 },
    { field: 'matrix',       header: 'Matrix',       width: 100 },
    { field: 'test',         header: 'Test',         width: 170 },
    { field: 'isGroup',      header: 'IsGroup',      width: 80, align: 'center',
      render: (r) => <input type="checkbox" checked={r.isGroup} readOnly disabled aria-label="IsGroup" /> },
    { field: 'method',       header: 'Method',       width: 120 },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 130 },
    { field: 'component',    header: 'Component',    width: 110 },
    { field: 'parameter',    header: 'Parameter',    width: 100 },
    { field: 'chargeTypeCode', header: 'ChargeType', width: 100, render: (r) => CHARGE_TYPE[r.chargeTypeCode] ?? '' },
    { field: 'tat',          header: 'TAT',          width: 100 },
    { field: 'priority',     header: 'Priority',     width: 90 },
    { field: 'priceCode',    header: 'PriceCode',    width: 110 },
    { field: 'qty',          header: 'Qty',          width: 70, align: 'right', type: 'number',
      summary: (list) => `Sum=${list.reduce((n, r) => n + r.qty, 0)}` },
    { field: 'unitPrice',    header: 'UnitPrice',    width: 100, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice),
      summary: (list) => `Sum=${money.format(list.reduce((n, r) => n + r.unitPrice, 0))}` },
    { field: 'tierNo',       header: 'TierNo',       width: 70, align: 'right', type: 'number' },
    { field: 'tierFrom',     header: 'From',         width: 70, align: 'right', type: 'number' },
    { field: 'tierTo',       header: 'To',           width: 70, align: 'right', type: 'number' },
    { field: 'totalTierPrice', header: 'TotalTierPrice', width: 120, align: 'right', type: 'number', render: (r) => money.format(r.totalTierPrice),
      summary: (list) => `Sum=${money.format(list.reduce((n, r) => n + r.totalTierPrice, 0))}` },
    { field: 'discount',     header: 'Discount(%)',  width: 110, align: 'right', type: 'number', render: (r) => r.discount.toFixed(2) },
    { field: 'finalAmount',  header: 'FinalAmount',  width: 120, align: 'right', type: 'number', render: (r) => money.format(r.finalAmount),
      summary: (list) => `Sum=${money.format(list.reduce((n, r) => n + r.finalAmount, 0))}` },
    { field: 'remark',       header: 'Remark',       width: 160 },
    { field: 'sort',         header: 'Sort',         width: 70, align: 'right', type: 'number' },
  ], []);

  const itemColumns: AppColumn<QuoteItemRow>[] = useMemo(() => [
    { field: 'itemCode',  header: 'ItemCode',  width: 120 },
    { field: 'itemName',  header: 'ItemName',  width: 220 },
    { field: 'category',  header: 'Category',  width: 140 },
    { field: 'unit',      header: 'Unit',      width: 90 },
    { field: 'unitPrice', header: 'UnitPrice', width: 110, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice) },
    { field: 'qty',       header: 'Qty',       width: 70, align: 'right', type: 'number',
      summary: (list) => `Sum=${list.reduce((n, i) => n + i.qty, 0)}` },
    { field: 'amount',    header: 'Amount',    width: 110, align: 'right', type: 'number', render: (r) => money.format(r.amount),
      summary: (list) => `Sum=${money.format(list.reduce((n, i) => n + i.amount, 0))}` },
    { field: 'discount',  header: 'Discount(%)', width: 110, align: 'right', type: 'number', render: (r) => r.discount.toFixed(2) },
    { field: 'finalAmount', header: 'FinalAmount', width: 120, align: 'right', type: 'number', render: (r) => money.format(r.finalAmount),
      summary: (list) => `Sum=${money.format(list.reduce((n, i) => n + i.finalAmount, 0))}` },
    { field: 'remark',    header: 'Remark',    width: 160 },
  ], []);

  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'analysis',   label: 'Analysis Pricing', count: rows.length },
    { id: 'items',      label: 'Item Charge',      count: items.length },
    { id: 'attachment', label: 'Attachment',       count: docCounts.attachments },
    { id: 'note',       label: 'Notes',            count: docCounts.notes },
  ];

  const q = quote;
  const recordName = q?.quoteID ?? quoteId ?? '';
  const statusText = q?.status ?? 'Canceled';

  const actionBar = (bottom: boolean) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      {/* Read-only view — no Save / New / Edit / Delete (owner request). */}
      <button type="button" className="sd-abtn" title={tip('Preview', 'alt+p')}
              onClick={() => void preview()} disabled={busy}>
        <span className="tb-i">👁</span> Preview
      </button>
      <button type="button" className="sd-abtn" title={tip('Refresh', 'alt+r')}
              onClick={() => void load()} disabled={loading}>
        <i className="ti ti-refresh tb-i-green" aria-hidden="true" /> Refresh
      </button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button type="button" className="sd-abtn cancel" title={tip('Close', 'esc')} onClick={onClose}>
        <span className="tb-i">↩︎</span> Close
      </button>
    </div>
  );

  return (
    // `qt-cancel-flat` gives THIS read-only form a plain-white, flat background
    // (owner request 2026-10-29) instead of the Open Quote detail's cards-on-grey
    // chrome — scoped in quotes.css so the editable Open Quote detail is unchanged.
    <div className="bl-page mlv-host qt-detail qt-cancel-flat">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          {/* Header — title + breadcrumb + prev/next record arrows */}
          <div className="sd-head">
            <div>
              <h2>
                Canceled Quote – {recordName || '…'}
                <span className={`adg-pill ${statusVariant(statusText)} sd-head-status`}>{statusText}</span>
              </h2>
              <div className="sd-bc">
                {onHome && <><a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a><i className="ti ti-chevron-right" /></>}
                <span>CRM</span>
                <i className="ti ti-chevron-right" />
                <span>Accounts</span>
                <i className="ti ti-chevron-right" />
                <span>Quotes</span>
                <i className="ti ti-chevron-right" />
                <a onClick={onClose} style={{ cursor: 'pointer' }}>Canceled Quotes</a>
                <i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="qt-headright">
              <AuditTrailButton variant="chip" formName={FORM_NAME} sourceId={id} />
              {onNavigate && siblingIds.length > 1 && (
                <div className="sd-navarrows">
                  <button type="button" className="sd-na" aria-label="Previous record"
                          title={tip('Previous record', 'alt+left')}
                          disabled={!navPrev} onClick={() => navPrev && onNavigate(navPrev)}>‹</button>
                  <button type="button" className="sd-na next" aria-label="Next record"
                          title={tip('Next record', 'alt+right')}
                          disabled={!navNext} onClick={() => navNext && onNavigate(navNext)}>›</button>
                </div>
              )}
            </div>
          </div>

          {actionBar(false)}

          <div className="sd-body sd-body--cards">
            {q && <>
            {section('Summary', [
              field('Quote ID', roText(q.quoteID)),
              field('Title', roText(q.title)),
              field('Client', roText(q.isProspect ? q.prospectClientName : q.clientName)),
              check('Is Prospect', q.isProspect),
              field('TAT', roText(q.tatName)),
              field('Payment Method', roText(q.paymentMethodName)),
              field('Expiration Date', roDate(q.expirationDate)),
              check('Cancel', q.cancel),
              q.cancel && field('Date Canceled', roDate(q.dateCanceled), 'date-canceled'),
              q.cancel && field('Cancel Reason', roText(q.cancelReason), 'cancel-reason'),
            ], [
              field('Project ID', roText(q.projectCode)),
              field('Project Name', roText(q.projectName)),
              field('Quoted Date', roDate(q.quotedDate)),
              field('Quoted By', roText(q.quotedByName)),
              field('Amount', roMoney(q.quotedAmount)),
              field('Remark', roArea(q.remark)),
            ])}

            {section('Totals', [
              field('Detailed Amount', roMoney(q.detailedAmount)),
              field('Discount Amount', roMoney(q.discountAmount)),
            ], [
              field('Discount %', roText((q.discount ?? 0).toFixed(2))),
              field('Amount', roMoney(q.totalAmount)),
            ])}

            {section('Contacts', [
              field('Primary Contact', roText(q.primaryContactName)),
              field('Email ID', roText(q.emailID)),
            ], [
              field('Cell Phone', roText(q.cellPhone)),
              field('Office Phone', roText(q.officePhone)),
            ])}

            <div className="sd-card-pair">
              {section('Bill To Address', [
                field('Street 1', roText(q.billStreet1)),
                field('Street 2', roText(q.billStreet2)),
                field('City', roText(q.billCity)),
                field('State', roText(q.billState)),
                field('Zip Code', roText(q.billZipCode)),
                field('Country', roText(q.billCountry)),
              ])}
              {section('Report to Address', [
                field('Street 1', roText(q.shipStreet1)),
                field('Street 2', roText(q.shipStreet2)),
                field('City', roText(q.shipCity)),
                field('State', roText(q.shipState)),
                field('Zip Code', roText(q.shipZipCode)),
                field('Country', roText(q.shipCountry)),
              ], undefined,
                <label className="sd-check">
                  <input type="checkbox" checked={q.sameAddress} readOnly disabled />
                  Same Address
                </label>)}
            </div>

            {section('Account Management', [
              field('Owner', roText(q.ownerName)),
              field('Credit', roText(q.creditName)),
            ], [
              field('Source Opportunity', roText(q.sourceOpportunityName)),
              field('Status', roText(statusText)),
            ])}
            </>}

            {/* The child tabs — the shared tab strip with its panel beneath it.
                All four panels stay mounted; the inactive ones are hidden. */}
            <section className="sd-card sd-card--tabs" aria-label="Pricing and documents">
              <div className="sd-assoc-tabbar sd-childtabbar" role="tablist">
                {tabs.map((t) => (
                  <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                          className={`sd-assoc-tab${tab === t.id ? ' active' : ''}`}
                          onClick={() => setTab(t.id)}>
                    {t.label}<span className="sd-assoc-tabn">{t.count}</span>
                  </button>
                ))}
              </div>

              <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" hidden={tab !== 'analysis'}>
                <AppDataGrid<QuoteAnalysisRow>
                  rows={rows}
                  columns={rowColumns}
                  getRowId={(r) => r.oid}
                  loading={loading}
                  maximizable
                  exportFilename={`quote-${recordName || 'canceled'}-analysis-pricing`}
                  emptyText="No price codes."
                />
              </div>

              <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" hidden={tab !== 'items'}>
                <AppDataGrid<QuoteItemRow>
                  rows={items}
                  columns={itemColumns}
                  getRowId={(r) => r.oid}
                  loading={loading}
                  maximizable
                  exportFilename={`quote-${recordName || 'canceled'}-item-charge`}
                  emptyText="No item charges."
                />
              </div>

              <div role="tabpanel" className="sd-childtabpanel qt-tabpanel"
                   hidden={tab !== 'attachment' && tab !== 'note'}>
                <PreLoginDocsPanel cocOid={id} owner={QUOTE_DOCS} embedded readOnly hideWriteActions
                                   activeTab={tab === 'note' ? 'note' : 'attachment'}
                                   onCounts={setDocCounts} />
              </div>
            </section>
          </div>

          {actionBar(true)}
        </div>
      </div>
    </div>
  );
}
